'use client';

import { useState, useEffect, useRef } from 'react';
import { signIn } from 'next-auth/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Delete } from 'lucide-react';
import { TenantBrandHeader } from '@/components/auth/tenant-brand-header';

const PIN_LENGTH = 6;

export default function StaffLoginPage() {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Arrived from /login with a username typed in the email box → prefill it
    // and jump straight to the PIN.
    const u = new URLSearchParams(window.location.search).get('u');
    if (u) setUsername(u.toLowerCase());
    else usernameRef.current?.focus();
  }, []);

  const submit = async (usernameVal: string, pinVal: string) => {
    setError('');
    setLoading(true);
    try {
      const result = await signIn('credentials', {
        username: usernameVal,
        password: pinVal,
        loginPath: 'staff',
        redirect: false,
      });
      if (result?.error) {
        // FF-542 — deactivated account gets its own message.
        setError(result.code === 'deactivated' ? 'Akun dinonaktifkan. Hubungi admin.' : 'Wrong username or PIN');
        setPin('');
        setLoading(false);
      } else {
        window.location.href = '/input';
      }
    } catch {
      setError('Something went wrong. Try again.');
      setLoading(false);
    }
  };

  const onPinDigit = (digit: string) => {
    if (loading) return;
    if (pin.length >= PIN_LENGTH) return;
    const next = pin + digit;
    setPin(next);
    if (next.length === PIN_LENGTH && username.trim()) {
      void submit(username.trim(), next);
    }
  };

  const onPinDelete = () => {
    if (loading) return;
    setPin((p) => p.slice(0, -1));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || pin.length !== PIN_LENGTH) {
      setError(`Enter username and ${PIN_LENGTH}-digit PIN`);
      return;
    }
    void submit(username.trim(), pin);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-8">
      <div className="w-full max-w-sm">
      <TenantBrandHeader />
      <Card className="w-full">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">Staff Login</CardTitle>
          <CardDescription>Sign in with your username and 6-digit PIN</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-5">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                id="username"
                ref={usernameRef}
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                className="w-full h-12 px-4 text-lg border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                disabled={loading}
              />
            </div>

            {/* PIN dots */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PIN</label>
              <div className="flex justify-center gap-3">
                {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-4 w-4 rounded-full border-2 ${
                      pin.length > i ? 'bg-emerald-600 border-emerald-600' : 'bg-gray-100 border-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* PIN keypad */}
            <div className="grid grid-cols-3 gap-2">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => onPinDigit(d)}
                  disabled={loading}
                  className="h-16 text-2xl font-semibold rounded-md bg-white border-2 border-gray-200 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50"
                >
                  {d}
                </button>
              ))}
              <div className="h-16" />
              <button
                type="button"
                onClick={() => onPinDigit('0')}
                disabled={loading}
                className="h-16 text-2xl font-semibold rounded-md bg-white border-2 border-gray-200 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50"
              >
                0
              </button>
              <button
                type="button"
                onClick={onPinDelete}
                disabled={loading || !pin}
                className="h-16 rounded-md bg-white border-2 border-gray-200 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 flex items-center justify-center"
                aria-label="Delete"
              >
                <Delete className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            {error && (
              <div className="text-center text-sm font-medium text-red-600">{error}</div>
            )}

            <Button
              type="submit"
              className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700"
              disabled={loading || !username.trim() || pin.length !== PIN_LENGTH}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>

            <p className="text-center text-xs text-gray-500 pt-2">
              Need office access? <a href="/login" className="text-emerald-700 underline">Sign in there</a>
            </p>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
